import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanityPage, SanityPageType } from '@data/sanity/queries/types/page'
import { getShopifyDomain } from '@lib/shopify/client'
import { getPreview } from '@lib/sanity/client'
import { getAllSlugs, getPage } from '@lib/sanity/page'
import { parseOptionalParameters } from '@lib/helpers'
import { getAllLocales, Locale } from '@lib/language'
import { getReviews, Reviews } from '@lib/review'
import { CustomStaticPropsContext, StaticPathWithSlugs } from '@lib/routes'

import Modules from '@modules/modules'
import Layout from '@components/layout'

interface PageProps {
  locale: Locale
  page: SanityPage
  site: SanitySiteFragment
  reviews?: Reviews
}

const Page = ({ page, site, reviews }: PageProps) => (
  <Layout page={page} site={site}>
    <Modules modules={page.modules} reviews={reviews} />
  </Layout>
)

interface PageParams extends ParsedUrlQuery {
  slugs: string[]
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<PageParams>): Promise<
  GetStaticPropsResult<PageProps>
> {
  const slugs = parseOptionalParameters(params?.slugs) ?? []
  const page = await getPage(
    locale,
    slugs.join('/'),
    getPreview(preview, previewData),
  )

  if (!page.page || page.page.type === SanityPageType.TEMPLATE) {
    return { notFound: true }
  }

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(locale)
  const reviews = await getReviews(
    page.page.modules ?? [],
    shopifyDomain,
    page.site.settings.stampedApiKey,
  )

  return {
    props: {
      locale,
      page: page.page,
      site: page.site,
      reviews,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<PageParams>
> {
  const allPages = await getAllSlugs('page')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlugs[] = allPages
    .map((page) => ({ ...page, locale: page.locale as Locale }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: {
        slugs: slug.split('/').filter((slug) => !!slug),
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default Page
